import React from 'react'
import classNames from 'classnames'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import './Pill.scss'

/**
 * @import import { Pill } from '@/components/pills/Pill'
 * @param {object} props
 * @param {boolean} [props.brand] Theme-colored variant
 * @param {boolean} [props.success] Green variant
 * @param {boolean} [props.warning] Yellow variant
 * @param {boolean} [props.caution] Orange variant
 * @param {boolean} [props.critical] Red variant
 * @param {boolean} [props.informative] Blue variant
 * @param {boolean} [props.contrast] Black background with white text
 * @param {boolean} [props.magical] Purple AI-feature variant
 * @param {node} [props.icon] Icon rendered beside the content; a `square` or `circular` Button here is sized to fit the pill
 * @param {string} [props.alignIcon] Side of the content the icon renders on: 'left' (default) or 'right'
 * @param {boolean} [props.emphasized] Bold text on a solid color fill instead of the soft background
 * @param {boolean} [props.large] Taller with larger text
 * @param {boolean} [props.small] Compact height
 * @param {boolean} [props.xs] Smallest height
 * @param {string} [props.size] 'large', 'small', or 'xs' as an alternative to the flag props
 * @param {boolean} [props.bold] Bold text
 * @param {boolean} [props.circular] Fully rounded dot shape
 * @param {node} [props.content] Alternative content; wins over children when both are passed
 * @param {node} [props.children] Pill content
 * @param {string} [props.className] Additional class names merged onto the pill
 * @param {boolean} [props.clickable] Hover border affordance for pills that act as buttons
 * @param {boolean} [props.transparent] No background fill
 * @param {boolean} [props.ghost] Dashed border, for placeholder or add-new pills
 * @param {boolean} [props.outlined] Solid border in the variant color
 * @param {string} [props.dataTestId] Overrides the default 'pill' test id
 * @param {string} [props.gradient] CSS background for a leading color dot
 * @param {boolean} [props.selected] Dark border and bold text marking the chosen pill
 * @param {boolean} [props.error] Critical background and border, matching field error styling
 * @param {boolean} [props.active] Border highlight on transparent pills
 * @param {node} [props.tooltip] Tooltip shown on hover
 */
export const Pill = ({
  brand,
  success,
  warning,
  caution,
  critical,
  informative,
  contrast,
  magical,
  header,
  icon,
  alignIcon = 'left',
  emphasized,
  large,
  small,
  xs,
  size,
  bold,
  circular,
  content,
  children,
  className,
  clickable,
  transparent,
  ghost,
  outlined,
  dataTestId,
  gradient,
  selected,
  error,
  active,
  tooltip,
  ...props
}) => {
  const pill = (
    <div
      data-testid={dataTestId || 'pill'}
      className={classNames('status-pill', className, {
        brand,
        success,
        warning,
        caution,
        critical,
        informative,
        contrast,
        magical,
        icon,
        emphasized,
        large: large || size === 'large',
        small: small || size === 'small',
        xs: xs || size === 'xs',
        bold,
        circular,
        clickable,
        transparent,
        ghost,
        selected,
        outlined,
        active,
        error,
      })}
      {...props}
    >
      {gradient && <div className="dot" style={{ background: gradient }} />}
      {alignIcon !== 'right' && icon}
      {content || children}
      {alignIcon === 'right' && icon}
    </div>
  )

  if (tooltip) {
    return (
      <InteractiveTooltip content={tooltip} placement="top">
        {pill}
      </InteractiveTooltip>
    )
  }

  return pill
}
